// 🔴 사원 결제 한도조회 — `employee-payment-popup` 의 Apply 시 / `/cart` 진입 시 /
//                       checkout 버튼 클릭 시 / cart-update 마다 호출.
//
// API: GET {api_base}/staff/remaining-public?shop_code=&employee_no=
//   응답 success: {"success": true, "remaining_amount": <number>}
//   응답 fail:    {"success": false}  (사유 미노출)
//
// 분기 로직:
//   - fail → "한도조회 실패" 모달 + 강제 normal 모드 전환 + reload
//   - success && (cart 정가 합 × 0.5) > remaining → "한도초과" 모달 + checkout 비활성화
//                + sessionStorage 에 exceed 상태 박제 → cart-update 마다 재발화로 회복 감지
//   - success && 한도 충분 → 통과
//
// 🔴 비교 기준: Shopify Function `employee-discount-function` 와 동일 로직 (`misto-msa` ad9bae74, 2026-05-22).
//    기준가 (reference) = line.compare_at_price 있으면 그것, 없으면 line.price (fallback)
//    사원가 = 기준가 × 0.5 (정가의 50%)
//    라인 할인액 = max(0, currentSubtotal - 사원가)  ← 음수면 0 으로 clamp (세일 > 50% 케이스)
//    discountAmount (총 할인액) > remaining → 한도초과
//
// 🔴 [중요] BE Function 과 base price 정합 필수. 한쪽만 바꾸면 표시/적용 불일치 회귀.
//    BE 커밋: `misto-msa` ad9bae74 "fix: base employee discount on compare-at price"
//    박제: wiki/docs/employee-sale-architecture.md §"할인 기준 base price"
//
// 박제: docs/be-handoff/2026-05-12-employee-payment-popup.md (옵션 A + 한도조회 통합)

class EmployeeLimitCheck {
  static SESSION_KEY = "employee_payment_mode";
  static FAIL_PENDING_KEY = "employee_limit_fail_pending";
  static EXCEED_STATE_KEY = "employee_limit_exceed_active";

  static get config() {
    return window.MISTO_EMPLOYEE || {};
  }

  static get isEmployeeMode() {
    try {
      return sessionStorage.getItem(this.SESSION_KEY) === "employee";
    } catch (e) {
      return false;
    }
  }

  static buildFailContext(detail = {}, caller = "limit_check") {
    const last = detail && detail.lastError ? detail.lastError : {};
    return {
      caller,
      reason: detail.reason || last.reason || "limit_check_fail",
      status: detail.status || last.status || null,
      via: detail.via || last.via || null,
      message: detail.message || detail.error || last.message || last.error || null,
    };
  }

  static saveFailContext(detail = {}, caller = "limit_check") {
    try {
      sessionStorage.setItem("diag_fail_ctx", JSON.stringify(this.buildFailContext(detail, caller)));
    } catch (e) {}
  }

  // ── API 호출 ───────────────────────────────────────────────
  // shop_code 가 region 결정 (HK online=10049, TW online=10050). FE 는 country param 미전송.
  // 🔴 (2026-05-28) 한도조회 — 재시도(기본 5회, 2026-07-06 상향). 재시도 간격 500ms. config 누락은 retry 무의미라 즉시 반환.
  //   🔴 (2026-07-06) 재검증 fail-OPEN 정책: 반환값에 항상 reason·reached_server 를 실어 evaluate 가 분기 판단.
  //     - 서버 명시 거부(HTTP 200 + {success:false}) = reason "body_not_success" → 재시도 무의미, 즉시 반환 → evaluate 가 fail-CLOSED.
  //     - transport 실패(fetch_exception / http_error / config 누락 / 재시도 소진) → evaluate 가 fail-OPEN (유효 모드 유지).
  //     ※ http_error 는 reached_server:true 지만 서버 "명시 거부" 가 아니므로 fail-OPEN — 판별 기준은 reason==="body_not_success" 단일.
  static async fetchRemaining(maxRetries = 5) {
    const { employee_store_code, employee_no, api_base } = this.config;
    if (!employee_store_code || !employee_no || !api_base) {
      console.warn("[limit-check] ❌ config 누락:", {
        employee_store_code, employee_no, api_base,
        hint: "MISTO_EMPLOYEE 글로벌 객체 확인 필요. 마이페이지에선 다른 endpoint 사용 가능성"
      });
      // 🔴 진단 — fail-OPEN (2026-07-06). 서버 미도달 = 명시 거부 아님 → 모드 유지. presence bool 이 누락 필드 식별.
      window.MISTO_DIAG && window.MISTO_DIAG.emit("employee_config_missing", { caller: "limit_check", fail_mode: "open" });
      return { success: false, reason: "config_missing", reached_server: false };
    }
    window.MISTO_DIAG && window.MISTO_DIAG.emit("limit_check_start", { caller: "limit_check", shop_code: employee_store_code });
    let lastResult = null;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      lastResult = await this._fetchRemainingOnce();
      if (lastResult.success) {
        return lastResult;
      }
      // 서버 명시 거부(200 {success:false}) → 재시도 무의미, 즉시 반환 (fail-CLOSED 대상).
      if (lastResult.reason === "body_not_success") {
        return lastResult;
      }
      console.warn(`[limit-check] ⚠ 시도 ${attempt}/${maxRetries} transport 실패:`, lastResult);
      if (attempt < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
    // 재시도 소진 — 마지막 transport 실패 결과에 exhausted 마커 부착 (reason/reached_server 보존 → evaluate 가 fail-OPEN).
    console.warn(`[limit-check] ❌ ${maxRetries}회 모두 transport 실패 → fail-OPEN 대상`);
    return Object.assign(
      { retries_exhausted: true },
      lastResult || { success: false, reason: "max_retries_exceeded", reached_server: false }
    );
  }

  // 🔴 (2026-07-03) same-origin 게이트웨이(/apps/sf) 우선 → cross-site direct 폴백.
  //   실패 모드 = "요청 도달·성공했으나 응답 미복귀"(해외 모바일 cross-site 왕복 실패,
  //   prod 로그로 확증). /apps/sf(Shopify App Proxy)로 태우면 Shopify edge가 왕복 대행 → 해결.
  //   proxy 404(게이트웨이 미설치)/네트워크에러 시 기존 direct 로 폴백 → 회귀 0.
  //   성공 경로는 _resolvedRemaining 에 캐시(페이지당). via(proxy|direct) 로 폴백률 측정.
  static get _remainingCandidates() {
    const { employee_store_code, employee_no, api_base } = this.config;
    const qs = `?shop_code=${encodeURIComponent(employee_store_code)}&employee_no=${encodeURIComponent(employee_no)}`;
    const all = [
      { via: "proxy", url: `/apps/sf/staff/remaining-public${qs}` },
      { via: "direct", url: `${api_base}/staff/remaining-public${qs}` },
    ];
    return this._resolvedRemaining ? [this._resolvedRemaining] : all;
  }

  static async _fetchRemainingOnce() {
    const candidates = this._remainingCandidates;
    let last = { success: false, reason: "fetch_exception", reached_server: false };
    for (let i = 0; i < candidates.length; i++) {
      const cand = candidates[i];
      const isLast = i >= candidates.length - 1;
      const t0 = Date.now();
      const diag = (fields) => window.MISTO_DIAG && window.MISTO_DIAG.emit("limit_check_result", Object.assign({ caller: "limit_check", via: cand.via, duration_ms: Date.now() - t0 }, fields));
      try {
        const res = await fetch(cand.url, { credentials: "same-origin" });
        // 404 = 경로 없음(게이트웨이 미설치) → 다음 후보(direct)로 폴백.
        if (res.status === 404 && !isLast) continue;
        if (!res.ok) {
          let bodyText = "";
          try { bodyText = await res.text(); } catch (_) {}
          console.warn("[limit-check] ❌ HTTP 실패:", { status: res.status, statusText: res.statusText, url: cand.url, body: bodyText.slice(0, 500) });
          // http_error 는 reached_server:true 지만 서버 "명시 거부" 아님 → transport 실패로 fail-OPEN 대상.
          diag({ result: "fail", reason: "http_error", http_status: res.status, reached_server: true });
          return { success: false, reason: "http_error", status: res.status, reached_server: true, via: cand.via, message: res.statusText };
        }
        this._resolvedRemaining = cand; // 성공 경로 캐시(페이지당) — 이후 재프로빙 생략
        const data = await res.json();
        const ok = !!(data && data.success);
        diag({
          result: ok ? "success" : "fail",
          reason: ok ? "ok" : "body_not_success",
          http_status: res.status, reached_server: true,
          remaining_amount: data ? data.remaining_amount : undefined,
        });
        if (ok) return data;
        // 🔴 HTTP 200 + {success:false} = 서버 명시 거부 → fail-CLOSED 유일 트리거. reason/reached_server 를 실어 evaluate 가 식별.
        const message = data && (data.message || data.detail || data.error);
        return Object.assign({}, data || {}, { success: false, reason: "body_not_success", reached_server: true, via: cand.via, message: message || null });
      } catch (e) {
        // 네트워크 에러 → 다음 후보 시도. 마지막이면 fail(reached_server:false = transport 실패 → fail-OPEN).
        if (!isLast) continue;
        console.warn("[limit-check] ❌ fetch 예외:", { error: e.message, url: cand.url });
        diag({ result: "fail", reason: "fetch_exception", http_status: 0, reached_server: false, error_code: e && e.name, error_message: e && e.message });
        last = { success: false, reason: "fetch_exception", reached_server: false, via: cand.via, error: e.message };
        return last;
      }
    }
    return last;
  }

  // 🔴 [중요] (2026-05-28 정책 재확정) 사원가 = 정가(compare_at_price) × 0.5. BE Function(compare_at 기준)과 정합.
  //   기준가 = compare_at_price > 0 이면 그것, 아니면 price (fallback).
  //   라인 할인액 = max(0, currentSubtotal - 정가×0.5)  ← 세일 > 50% 면 0 (추가 할인 없음, BE clamp 와 동일).
  //   총 할인액 = Σ 라인 할인액  → remaining 과 비교.
  //   #24 의 price 기준은 BE 와 불일치라 롤백. 회귀 박제: wiki/docs/employee-sale-base-price-policy-change-2026-05-28.md
  static async getCartEmployeeDiscount() {
    try {
      const res = await fetch(MISTO.localeUrl('/cart.js'));
      if (!res.ok) return null;
      const cart = await res.json();
      let total = 0;
      for (const item of cart.items || []) {
        const qty = item.quantity || 0;
        const priceCents = item.original_price ?? item.price ?? 0;
        const compareAtCents = item.compare_at_price ?? null;
        const currentSubtotal = (priceCents * qty) / 100;
        const referenceSubtotal = compareAtCents && compareAtCents > 0
          ? (compareAtCents * qty) / 100
          : currentSubtotal;
        const employeePrice = referenceSubtotal * 0.5;
        const lineDiscount = Math.max(0, currentSubtotal - employeePrice);
        total += lineDiscount;
      }
      return total;
    } catch (e) {
      return null;
    }
  }

  // ── 평가 + 분기 ──────────────────────────────────────────
  static async evaluate({ skipCartCheck = false, caller = "limit_check" } = {}) {
    if (!this.isEmployeeMode) return { ok: true, skipped: true };

    const result = await this.fetchRemaining();
    if (!result.success) {
      // 🔴 (2026-07-06) 재검증 fail-OPEN 정책 — fail-CLOSED(모드 clear + fail 모달)는 서버 명시 거부(HTTP 200 {success:false})일 때만.
      //   transport 실패(network/http_error/config 누락/재시도 소진)는 유효 모드 유지 + 모달 X.
      //   근거: iOS stale keep-alive 즉시 실패가 유효 모드를 revoke → "매번 모달" 회귀. 실제 한도는 ERP 정산이 강제하므로 안전.
      const serverRejected = result.reason === "body_not_success";
      if (!serverRejected) {
        console.warn("[limit-check] ⚠ 재검증 transport 실패 — fail-OPEN (모드 유지, 모달 X):", {
          reason: result.reason, status: result.status, error: result.error, reached_server: !!result.reached_server,
        });
        window.MISTO_DIAG && window.MISTO_DIAG.emit("revalidation_fail_open", {
          caller, reason: result.reason, reached_server: !!result.reached_server, retries_exhausted: !!result.retries_exhausted,
        });
        return { ok: true, failOpen: true, reason: result.reason };
      }
      console.warn("[limit-check] ❌ 재검증 서버 거부(200 {success:false}) — 일반 모드로 fallback:", { reason: result.reason });
      this.handleFail(result, caller);
      return { ok: false, reason: "fail", detail: result };
    }

    if (skipCartCheck) {
      // Apply 시점에는 cart total 비교 X (popup 닫기 직후 cart 가 아직 mode 반영 안 된 상태 가능)
      this.clearExceedIfAny();
      return { ok: true };
    }

    const discountAmount = await this.getCartEmployeeDiscount();
    if (discountAmount === null) return { ok: true, error: "cart_fetch_failed" };

    const remaining = parseFloat(result.remaining_amount);

    if (discountAmount > remaining) {
      this.handleExceed({ discountAmount, remaining });
      return { ok: false, reason: "exceed" };
    }

    // 한도 회복 — 이전 exceed 상태였으면 복구
    this.clearExceedIfAny();
    return { ok: true };
  }

  // ── 한도조회 실패 ────────────────────────────────────────
  //   sessionStorage 를 미선택 (removeItem) 으로 → 사용자가 다시 결정 가능.
  //   fail_pending flag 박제 → reload 후 fail 모달만 노출.
  //   popup 은 fail_pending 동안 connectedCallback 에서 노출 차단됨.
  static handleFail(detail = {}, caller = "limit_check") {
    try {
      sessionStorage.removeItem(this.SESSION_KEY);
      sessionStorage.setItem(this.FAIL_PENDING_KEY, "1");
      sessionStorage.removeItem(this.EXCEED_STATE_KEY);
      // 🔴 진단 — reload 로 흔적이 지워지기 전 원인 컨텍스트 박제 (fail_modal_shown 이 읽음).
      this.saveFailContext(detail, caller);
    } catch (e) {}
    window.MISTO_DIAG && window.MISTO_DIAG.emit("employee_mode_cleared", { reason: "fail", prev_mode: "employee", caller });
    document.documentElement.classList.remove("employee-discount-applied");
    // cart attribute 도 클리어 (BE Function 이 할인 미적용 하도록)
    this.markCartNormal().finally(() => {
      // reload — 페이지 새로고침 시 fail pending flag 보고 모달 표시.
      window.location.reload();
    });
  }

  // ── 한도초과 ─────────────────────────────────────────────
  //   sessionStorage 에 exceed 상태 박제. cart-update 마다 재평가 → 회복 시 자동 해제.
  static handleExceed({ discountAmount, remaining }) {
    try {
      sessionStorage.setItem(this.EXCEED_STATE_KEY, "1");
    } catch (e) {}
    this.disableCheckoutButtons();
    this.showModal("EmployeeLimitExceedModal", { discountAmount, remaining });
  }

  static clearExceedIfAny() {
    let was = false;
    try {
      was = sessionStorage.getItem(this.EXCEED_STATE_KEY) === "1";
      sessionStorage.removeItem(this.EXCEED_STATE_KEY);
    } catch (e) {}
    if (was) {
      this.enableCheckoutButtons();
      this.hideModal("EmployeeLimitExceedModal");
    }
  }

  // ── cart attribute 마킹 (BE Function 신호) ────────────────
  static async markCartNormal() {
    window.MISTO_DIAG && window.MISTO_DIAG.emit("cart_attribute_update_start", { target_mode: "", caller: "limit_check" });
    try {
      const res = await fetch(MISTO.localeUrl('/cart/update.js'), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          attributes: { employee_payment_mode: "", is_employee_sale: "false" }
        }),
      });
      window.MISTO_DIAG && window.MISTO_DIAG.emit("cart_attribute_update_result", { target_mode: "", ok: res.ok, http_status: res.status, caller: "limit_check" });
    } catch (e) {
      window.MISTO_DIAG && window.MISTO_DIAG.emit("cart_attribute_update_result", { target_mode: "", ok: false, reason: "fetch_exception", error_code: e && e.name, caller: "limit_check" });
    }
  }

  // ── checkout 버튼 비활성화 / 활성화 ─────────────────────
  //   cart 페이지의 checkout submit, cart drawer 의 checkout link, 추가 sticky checkout 도 포함.
  static get checkoutSelector() {
    return 'button[name="checkout"], a[href*="/checkout"], a[href="/cart"][data-checkout], button[data-cart-checkout]';
  }

  static disableCheckoutButtons() {
    document.querySelectorAll(this.checkoutSelector).forEach((el) => {
      el.dataset.employeeLimitExceeded = "1";
      if ("disabled" in el) el.disabled = true;
      el.setAttribute("aria-disabled", "true");
      el.classList.add("employee-limit-exceeded-disabled");
    });
  }

  static enableCheckoutButtons() {
    document.querySelectorAll('[data-employee-limit-exceeded="1"]').forEach((el) => {
      delete el.dataset.employeeLimitExceeded;
      if ("disabled" in el) el.disabled = false;
      el.removeAttribute("aria-disabled");
      el.classList.remove("employee-limit-exceeded-disabled");
    });
  }

  // ── 모달 트리거 ──────────────────────────────────────────
  static showModal(modalId, data) {
    const modal = document.getElementById(modalId);
    if (!modal) {
      console.warn(`[limit-check] modal not found: ${modalId}`);
      return;
    }
    if (data && typeof modal.injectData === "function") {
      modal.injectData(data);
    }
    if (typeof modal.show === "function") {
      modal.show();
    }
  }

  static hideModal(modalId) {
    const modal = document.getElementById(modalId);
    if (modal && typeof modal.hide === "function") {
      modal.hide();
    }
  }

  // ── fetch interceptor (cart-update 트래킹) ────────────────
  //   사원 모드 일 때만 동작. /cart/{update,add,change,clear}.js 호출 후 한도 재평가.
  //   debounce 300ms — 연속 변경 합쳐서 한 번만 발화.
  static installFetchInterceptor() {
    if (this._fetchPatched) return;
    this._fetchPatched = true;
    const originalFetch = window.fetch.bind(window);
    const self = this;
    window.fetch = async function (...args) {
      const response = await originalFetch(...args);
      try {
        const url = args[0];
        const urlStr = typeof url === "string" ? url : url?.url || "";
        if (self.isEmployeeMode && /\/cart\/(update|add|change|clear)\.js/.test(urlStr)) {
          clearTimeout(self._cartUpdateTimer);
          self._cartUpdateTimer = setTimeout(() => self.evaluate({ caller: "cart_update" }), 300);
        }
      } catch (e) {}
      return response;
    };
  }

  // ── checkout 버튼 클릭 hook ─────────────────────────────
  //   exceed 상태면 click 차단. exceed 아니어도 클릭 시 재검증 (마지막 안전망).
  static installCheckoutHook() {
    if (this._checkoutHooked) return;
    this._checkoutHooked = true;
    document.addEventListener(
      "click",
      (e) => {
        if (!this.isEmployeeMode) return;
        const target = e.target.closest('button[name="checkout"], a[href*="/checkout"]');
        if (!target) return;
        const blocked = sessionStorage.getItem(this.EXCEED_STATE_KEY) === "1";
        // 🔴 진단 — unload 인접(navigation) → sendBeacon 이 살려 보냄.
        window.MISTO_DIAG && window.MISTO_DIAG.emit("checkout_click", { is_employee_mode: true, exceed_active: blocked, blocked: blocked });
        // 1) 이미 exceed 상태면 즉시 차단
        if (blocked) {
          e.preventDefault();
          e.stopPropagation();
          this.showModal("EmployeeLimitExceedModal");
          return;
        }
        // 2) 안전망 — 백그라운드 재검증. 위배 발견 시 강제 reload (사용자가 다시 클릭 필요)
        window.MISTO_DIAG && window.MISTO_DIAG.emit("checkout_revalidation_start", { caller: "checkout" });
        Promise.resolve(this.evaluate({ caller: "checkout" })).then((r) => {
          window.MISTO_DIAG && window.MISTO_DIAG.emit("checkout_revalidation_result", { caller: "checkout", ok: !!(r && r.ok), reason: (r && r.reason) || "ok", exceeded: !!(r && r.reason === "exceed") });
        });
      },
      true
    );
  }

  // ── 강제 모달 노출 (개발/테스트용 쿼리 파라미터) ──────────
  //   사용법:
  //     ?force_modal=popup   → 사원판매 선택 팝업 강제 노출 (sessionStorage 정리)
  //     ?force_modal=fail    → 한도조회 실패 모달 강제 노출
  //     ?force_modal=exceed  → 한도 초과 모달 강제 노출 (dummy stats)
  static handleForceModalQuery() {
    let force = null;
    try {
      force = new URLSearchParams(location.search).get("force_modal");
    } catch (e) {}
    if (!force) return false;

    if (force === "popup") {
      try {
        sessionStorage.removeItem(this.SESSION_KEY);
        sessionStorage.removeItem(this.FAIL_PENDING_KEY);
      } catch (e) {}
      setTimeout(() => {
        const popup = document.querySelector("employee-payment-popup");
        if (popup && typeof popup.show === "function") popup.show();
      }, 0);
      return true;
    }

    if (force === "fail") {
      setTimeout(() => this.showModal("EmployeeLimitFailModal", {
        caller: "force_modal",
        reason: "fetch_exception",
        status: 0,
        via: "direct",
        message: "Preview detail",
      }), 0);
      return true;
    }

    if (force === "exceed") {
      setTimeout(() => {
        this.showModal("EmployeeLimitExceedModal", {
          discountAmount: 1000,
          remaining: 500,
        });
      }, 0);
      return true;
    }

    return false;
  }

  // ── 사원 + 모드 미선택 hook ──────────────────────────────
  //   사원인데 employee/normal 미선택 상태에서 cart 의 checkout 버튼 클릭 시
  //   → 클릭 차단 + employee-payment-popup 강제 노출.
  //   → 사원의 mode 결정 없이 결제 진입 방지 (선택 후 cart.attribute 마킹 + Function 발화 보장).
  static installEmployeeUnselectedHook() {
    if (this._unselectedHooked) return;
    this._unselectedHooked = true;
    document.addEventListener(
      "click",
      (e) => {
        // 비-사원 → skip (theme.liquid inline 이 비-사원 케이스 normalize 처리)
        if (!this.config.is_employee) return;
        // 이미 선택됨 → skip (employee 모드는 별도 installCheckoutHook 이 한도 검증)
        let stored = null;
        try {
          stored = sessionStorage.getItem(this.SESSION_KEY);
        } catch (e) {}
        if (stored === "employee" || stored === "normal") return;

        const target = e.target.closest('button[name="checkout"], a[href*="/checkout"]');
        if (!target) return;

        e.preventDefault();
        e.stopPropagation();

        // popup 강제 노출 — 사용자 선택 후 reload → cart.attribute 마킹 → checkout 재시도 가능
        const popup = document.querySelector("employee-payment-popup");
        if (popup && typeof popup.show === "function") {
          popup.show();
        }
      },
      true
    );
  }

  // ── 초기화 ───────────────────────────────────────────────
  static init() {
    // 강제 모달 노출 — 사원 여부 무관하게 동작 (테스트/QA용)
    if (this.handleForceModalQuery()) return;

    if (!this.config.is_employee) return;

    // 1. fail pending flag — 직전 evaluate 가 fail 처리 후 reload 한 경우
    try {
      if (sessionStorage.getItem(this.FAIL_PENDING_KEY) === "1") {
        sessionStorage.removeItem(this.FAIL_PENDING_KEY);
        // 🔴 진단 — reload 넘어온 원인 컨텍스트 복원 후 fail_modal_shown self-attribute.
        let ctx = null;
        try { ctx = JSON.parse(sessionStorage.getItem("diag_fail_ctx") || "null"); sessionStorage.removeItem("diag_fail_ctx"); } catch (_) {}
        window.MISTO_DIAG && window.MISTO_DIAG.emit("fail_modal_shown", { fail_source: (ctx && ctx.caller) || "unknown", orig_reason: ctx && ctx.reason });
        // 모달은 customElement registration 후라야 보임 → 약간 지연
        setTimeout(() => this.showModal("EmployeeLimitFailModal", ctx), 0);
      }
    } catch (e) {}

    // 2. /cart 페이지 진입 시 즉시 평가
    if (window.location.pathname === "/cart" && this.isEmployeeMode) {
      // 모달이 mount 된 후 평가하도록 다음 frame 으로 지연
      setTimeout(() => this.evaluate({ caller: "cart_init" }), 0);
    }

    // 3. 이미 exceed 상태로 진입한 경우 (다른 페이지에서 exceed 후 navigate) checkout 비활성화 복구
    if (this.isEmployeeMode) {
      try {
        if (sessionStorage.getItem(this.EXCEED_STATE_KEY) === "1") {
          // DOM 이 충분히 mount 된 후 비활성화 (다음 frame)
          setTimeout(() => this.disableCheckoutButtons(), 0);
        }
      } catch (e) {}
    }

    // 4. fetch interceptor 설치 (사원 여부 무관하게 — 사원 모드 진입 시 활성화)
    this.installFetchInterceptor();

    // 5. checkout 버튼 hook (한도 검증 — 이미 employee 모드 진입한 사용자 대상)
    this.installCheckoutHook();

    // 6. 사원 + 미선택 hook (PDP·quick-cart-drawer 의 Buy It Now / Shop Pay 등 가로채기)
    this.installEmployeeUnselectedHook();

  }
}

window.EmployeeLimitCheck = EmployeeLimitCheck;

// ── Modal custom elements ─────────────────────────────────
//   sections/employee-limit-modal.liquid 의 markup 과 함께 사용.
//   ModalDialog 패턴 재활용 — show()/hide() 가 [open] attribute 토글.

class EmployeeLimitFailModal extends ModalDialog {
  constructor() {
    super();
    this.querySelectorAll(".js-button-close, .js-button-ok").forEach((btn) => {
      btn.addEventListener("click", () => this.hide());
    });
  }
  connectedCallback() {
    super.connectedCallback();
    // Theme editor 미리보기
    if (window.Shopify && Shopify.designMode && this.dataset.openInDesignMode === "true") {
      this.show();
    }
  }

  injectData(data) {
    const el = this.querySelector("[data-limit-fail-detail]");
    if (!el) return;
    const detail = data || {};
    const parts = [];
    if (detail.caller) parts.push(`caller=${detail.caller}`);
    if (detail.reason) parts.push(`reason=${detail.reason}`);
    if (detail.status !== null && detail.status !== undefined) parts.push(`status=${detail.status}`);
    if (detail.via) parts.push(`via=${detail.via}`);
    if (detail.message) parts.push(`message=${String(detail.message).slice(0, 80)}`);
    if (!parts.length) {
      el.hidden = true;
      el.textContent = "";
      return;
    }
    el.textContent = `Details: ${parts.join(" · ")}`;
    el.hidden = false;
  }
}
customElements.define("employee-limit-fail-modal", EmployeeLimitFailModal);

class EmployeeLimitExceedModal extends ModalDialog {
  constructor() {
    super();
    this.querySelectorAll(".js-button-close").forEach((btn) => {
      btn.addEventListener("click", () => this.hide());
    });

    // 사원판매 취소 → 일반결제로 변경 (sessionStorage='normal' + cart attribute 클리어 + reload)
    this.querySelectorAll(".js-action-cancel-employee").forEach((btn) => {
      btn.addEventListener("click", () => this.cancelEmployeeMode());
    });

    // 카트 비우기 → /cart/clear.js + reload
    this.querySelectorAll(".js-action-clear-cart").forEach((btn) => {
      btn.addEventListener("click", () => this.clearCart());
    });
  }

  // 사원판매 모드 취소 (일반결제로 진행)
  async cancelEmployeeMode() {
    try {
      sessionStorage.setItem(EmployeeLimitCheck.SESSION_KEY, "normal");
      sessionStorage.removeItem(EmployeeLimitCheck.EXCEED_STATE_KEY);
    } catch (e) {}
    document.documentElement.classList.remove("employee-discount-applied");
    try {
      await fetch(MISTO.localeUrl('/cart/update.js'), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          attributes: { employee_payment_mode: "", is_employee_sale: "false" },
        }),
      });
    } catch (e) {}
    window.location.reload();
  }

  // 카트 비우기 (사원판매 모드 유지)
  async clearCart() {
    try {
      sessionStorage.removeItem(EmployeeLimitCheck.EXCEED_STATE_KEY);
    } catch (e) {}
    try {
      await fetch(MISTO.localeUrl('/cart/clear.js'), { method: "POST" });
    } catch (e) {}
    window.location.reload();
  }

  // EmployeeLimitCheck.showModal(id, data) 가 호출. discountAmount / remaining 표시.
  injectData({ discountAmount, remaining }) {
    const fmt = (v) =>
      "HK$ " +
      Number(v).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    const cur = this.querySelector("[data-limit-current]");
    const rem = this.querySelector("[data-limit-remaining]");
    if (cur) cur.textContent = fmt(discountAmount);
    if (rem) rem.textContent = fmt(remaining);
  }
  connectedCallback() {
    super.connectedCallback();
    // Theme editor 미리보기
    if (window.Shopify && Shopify.designMode && this.dataset.openInDesignMode === "true") {
      this.injectData({ discountAmount: 12345, remaining: 67890 });
      this.show();
    }
  }
}
customElements.define("employee-limit-exceed-modal", EmployeeLimitExceedModal);

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => EmployeeLimitCheck.init());
} else {
  EmployeeLimitCheck.init();
}
